import React from 'react';
import { useTranslation } from 'react-i18next';
import { isAfter, isSameDay } from 'date-fns';

import { OmaLummeApp, EvChargingApp } from '../../omaLummeContext';

// Types.
import { Contract, EstimatedAnnualUsage } from '../../types/contract';
// Utils.
import { generateAddress } from '../../utils/address';
import { contractIsEvCharging, formatTag } from '../../utils/evCharging';

type contractProps = {
  contract: Contract;
};

/**
 * The title for and internals of a contract dropdown.
 */
const CustomerContractDetails: React.FunctionComponent<contractProps> = (props: contractProps) => {
  const { t } = useTranslation();
  const { contract } = props;

  const { basicData } = OmaLummeApp.useState();
  const { evChargerInfo } = EvChargingApp.useState();

  const contractCustomerDetails: React.JSX.Element[] = [];

  const customerCompany = contract.meteringPointDetails?.gridOwner.name;

  // We need these in a specific order but some of the values may be empty.
  // So we generate the array in steps.
  const contractDetails = [];
  if (contract.contractParties && contract.contractParties.length > 1) {
    contractDetails.push({
      translationString: 'additionalOwner',
      value: contract.contractParties.map((owner) => (
        <React.Fragment key={owner}>
          {owner}
          <br />
        </React.Fragment>
      )),
    });
  }
  contractDetails.push({
    translationString: 'contractCode',
    value: contract.contractCode,
  });
  if (contract.meteringPoint) {
    contractDetails.push({
      translationString: 'address',
      value: generateAddress(contract.meteringPoint, false),
    });
  }
  if (contract.meteringPointId) {
    contractDetails.push({
      translationString: 'meteringpointId',
      value: contract.meteringPointId,
    });
  }
  const basicContract = basicData && basicData.find((c) => c.contractCode === contract.contractCode);
  const basicContractIsEvCharging = basicContract && contractIsEvCharging(basicContract) ? true : false;

  if (evChargerInfo !== undefined && basicContractIsEvCharging) {
    const tag = evChargerInfo.tags.find((tag) => tag.idTag === contract.rfid);

    contractDetails.push({
      translationString: 'rfidTag',
      value: tag ? `${formatTag(tag.idTag)} (${tag.tokenName})` : ``,
    });
  }

  if (contract.meteringPointDetails && contract.meteringPointDetails.estimatedAnnualUsage.length > 0 && contract.preferredMeasuringType) {
    // We define a fake one to avoid used before assigned error.
    const fakeAnual = {
      meteringPointNo: 0,
      counterText: '',
      amount: 0,
      dateFrom: '1970-01-01T00:00:00',
    };

    contract.meteringPointDetails.estimatedAnnualUsage.sort((a, b) => (a.dateFrom < b.dateFrom ? 1 : b.dateFrom < a.dateFrom ? -1 : 0));

    if (contract.preferredMeasuringType === '1' || contract.preferredMeasuringType === '4') {
      let activeAllTime: EstimatedAnnualUsage = fakeAnual;

      contract.meteringPointDetails.estimatedAnnualUsage.map((estimation, index): undefined => {
        if (index > 1) {
          return undefined;
        } else if (isAfter(new Date(estimation.dateFrom), new Date(activeAllTime.dateFrom))) {
          activeAllTime = { ...estimation };
        } else if (
          isSameDay(new Date(estimation.dateFrom), new Date(activeAllTime.dateFrom)) ||
          (activeAllTime.counterText.toLowerCase() !== 'kwh reading measured' && estimation.counterText.toLowerCase() !== 'kwh reading measured')
        ) {
          activeAllTime.amount += estimation.amount;
        }
        return undefined;
      });

      if (isAfter(new Date(activeAllTime.dateFrom), new Date('1970-01-01T00:00:00'))) {
        contractDetails.push({
          translationString: 'yearlyConsumption',
          value: `${activeAllTime.amount} ${t('contracts:customer.consumptionUnit')}`,
        });
      }
    }

    if (contract.preferredMeasuringType === '2' || contract.preferredMeasuringType === '3') {
      let activeDay: EstimatedAnnualUsage = fakeAnual;
      let activeNight: EstimatedAnnualUsage = fakeAnual;
      let activeWinterDay: EstimatedAnnualUsage = fakeAnual;
      let activeOtherTime: EstimatedAnnualUsage = fakeAnual;
      let activeAllTime: EstimatedAnnualUsage = fakeAnual;
      contract.meteringPointDetails.estimatedAnnualUsage.map((estimation, index): undefined => {
        if (index > 1) {
          return undefined;
        }
        let isDay = false;
        let isNight = false;
        let isWinterDay = false;
        let isOtherTime = false;
        // Anoyingly the only way to detect if it is night or day is from a string.
        if (estimation.counterText.toLowerCase().includes('measured day')) {
          isDay = true;
        } else if (estimation.counterText.toLowerCase().includes('measured night')) {
          isNight = true;
        } else if (estimation.counterText.toLowerCase().includes('measured winter day')) {
          isWinterDay = true;
        } else if (estimation.counterText.toLowerCase().includes('measured other')) {
          isOtherTime = true;
        }
        if (isDay && isAfter(new Date(estimation.dateFrom), new Date(activeDay.dateFrom))) {
          activeDay = estimation;
        } else if (isNight && isAfter(new Date(estimation.dateFrom), new Date(activeNight.dateFrom))) {
          activeNight = estimation;
        } else if (isWinterDay && isAfter(new Date(estimation.dateFrom), new Date(activeWinterDay.dateFrom))) {
          activeWinterDay = estimation;
        } else if (isOtherTime && isAfter(new Date(estimation.dateFrom), new Date(activeOtherTime.dateFrom))) {
          activeOtherTime = estimation;
        } else if (!isNight && !isDay && !isWinterDay && !isOtherTime && isAfter(new Date(estimation.dateFrom), new Date(activeAllTime.dateFrom))) {
          activeAllTime = estimation;
        }
        return undefined;
      });

      if (isAfter(new Date(activeAllTime.dateFrom), new Date('1970-01-01T00:00:00'))) {
        contractDetails.push({
          translationString: 'yearlyConsumption',
          value: `${activeAllTime.amount} ${t('contracts:customer.consumptionUnit')}`,
        });
      }
      if (isAfter(new Date(activeDay.dateFrom), new Date('1970-01-01T00:00:00'))) {
        contractDetails.push({
          translationString: 'yearlyConsumptionDay',
          value: `${activeDay.amount} ${t('contracts:customer.consumptionUnit')}`,
        });
      }
      if (isAfter(new Date(activeNight.dateFrom), new Date('1970-01-01T00:00:00'))) {
        contractDetails.push({
          translationString: 'yearlyConsumptionNight',
          value: `${activeNight.amount} ${t('contracts:customer.consumptionUnit')}`,
        });
      }
      if (isAfter(new Date(activeWinterDay.dateFrom), new Date('1970-01-01T00:00:00'))) {
        contractDetails.push({
          translationString: 'yearlyConsumptionWinterDay',
          value: `${activeWinterDay.amount} ${t('contracts:customer.consumptionUnit')}`,
        });
      }
      if (isAfter(new Date(activeOtherTime.dateFrom), new Date('1970-01-01T00:00:00'))) {
        contractDetails.push({
          translationString: 'yearlyConsumptionOtherTime',
          value: `${activeOtherTime.amount} ${t('contracts:customer.consumptionUnit')}`,
        });
      }
    }
  }
  if (customerCompany) {
    contractDetails.push({
      translationString: `company`,
      value: customerCompany,
    });
  }

  contractDetails.map((element, key): undefined => {
    contractCustomerDetails.push(
      <div key={key}>
        <span>{t(`contracts:customer.${element.translationString}`)}</span>
        <span>{element.value}</span>
      </div>,
    );
    return undefined;
  });

  return <div className="contract-customer-details">{contractCustomerDetails}</div>;
};

export default CustomerContractDetails;
